import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { formatDay } from '@kidshome/i18n';
import {
  useDayTasks,
  useMissions,
  useChildBalance,
  useTodayHabits,
  useWeekAgenda,
  resolveErrorMessage,
  type DayTasksResponse,
  type OccurrenceResponse,
  type OccurrenceResponseStatus,
} from '@kidshome/api-contract';
import { useAuthStore, useCan, useFeature } from '@/entities/session';
import {
  LoadingState,
  ErrorState,
  EmptyState,
  CoinIcon,
  CashIcon,
  Money,
  KidPageHeader,
} from '@/shared/ui';
import { choreGlyph } from '@/shared/ui/glyphs';
import { CameraGlyph, RedoGlyph } from '@/shared/ui/kid-chore-glyphs';
import { ConceptIcon } from '@/shared/ui/ConceptIcon';
import { choreArt } from '@/assets/icons';
import { Feature } from '@/shared/lib/features';
import { useHero, HeroCurrencyProvider } from '@/entities/hero';
import { ShopStarBalance } from '@/widgets/cosmetics-shop';
import { useHouseholdContext } from '@/shared/lib/household';
import { localDateISO, weekStartISO, addDaysISO, parseLocalDate } from '@/shared/lib/dates';
import { tDynamic } from '@/shared/lib/i18n';
import { isChoreDoneByChild, BUCKET_ORDER } from '@/shared/lib/chore-status';
import { Perm } from '@/shared/lib/permission-codes';
import { HabitBoard } from '@/widgets/habit-board';
import { HeroPresence } from '@/widgets/hero-presence';
import { KidQuestCard } from '@/widgets/family-quest';
import { WeekStrip } from '@/widgets/week-agenda/WeekStrip';
import { useCompleteTaskAction } from '@/features/complete-task';
import { useRedoTaskAction } from '@/features/redo-task';
import { useUncompleteTaskAction } from '@/features/uncomplete-task';
import { PhotoCompleteButton } from '@/features/photo-complete-task';
import { MissionsDigest } from '@/widgets/missions';
import '@/pages/today/today.css';

/**
 * The kid's Home — a navigable single-day calendar (ADR-0083), rebuilt to reproduce the approved
 * mockup `docs/hero-redesign/kid-preview/02-home.html`: a day greeting, the week navigator + 7-day
 * strip, the status legend, the one-line hero presence strip, then today's chores as
 * warm occurrence cards grouped into Morning / Afternoon / Evening buckets, closing on a kind,
 * no-FOMO footer. `selectedDate` is the only state; the strip window is the week that contains it.
 * TODAY is the full, editable day (hero presence strip + the optimistic complete control
 * + today's habits); PAST and FUTURE days render the chores READ-ONLY (the chore detail and the server
 * both enforce "a child acts on today only").
 */
export function TodayPage(): React.ReactElement {
  const { t } = useTranslation('family');
  const user = useAuthStore((s) => s.user);
  const { can } = useCan();
  const { locale, timeZone } = useHouseholdContext();
  const today = localDateISO(timeZone);
  const assigneeId = user?.id ?? '';

  const [selectedDate, setSelectedDate] = useState(today);
  const isToday = selectedDate === today;
  const weekStart = weekStartISO(selectedDate, locale);

  // `today` may briefly be computed from the DEVICE timezone (a fallback while `GET /v1/household` is
  // in flight — see useHouseholdContext) before the real household timezone resolves. If the two
  // disagree on the current calendar date, the frozen `useState(today)` initial value goes stale the
  // moment the real value lands: `isToday` flips to false with no user navigation, silently freezing
  // the kid's day on the wrong date. Resync whenever `today` itself changes AND the page was still
  // tracking the PREVIOUS `today` (i.e. the kid hadn't navigated away) — a deliberate "next/prev week"
  // tap is never clobbered, because `selectedDate` no longer matches `prevToday` once that happens.
  const prevToday = useRef(today);
  useEffect(() => {
    if (prevToday.current !== today) {
      setSelectedDate((d) => (d === prevToday.current ? today : d));
      prevToday.current = today;
    }
  }, [today]);

  const day = useDayTasks(assigneeId, selectedDate, { enabled: !!assigneeId });
  // The week the strip is showing — its per-day status dots. Its own cache (keyed by weekStart) is
  // invalidated by every task write, so completing today's chore updates the strip dot too.
  const week = useWeekAgenda(assigneeId, weekStart, { enabled: !!assigneeId });
  // The balance both shows the kid their coins AND seeds the cache so completing a chore bumps the
  // visible balance with the task list (optimistic; reconciled on settle). Only meaningful on today.
  const balance = useChildBalance(assigneeId, { enabled: !!assigneeId && isToday });
  // Today's habits, gated on the own-check capability so only a child (who self-scopes) fetches them.
  const canCheckHabits = can(Perm.HabitCheckOwn);
  const habits = useTodayHabits({ enabled: canCheckHabits && isToday });
  // Open "missions" (ADR-0141): anytime one-time chores in their own calm lane, shown on TODAY only
  // (they have no calendar day, so a past/future day view never carries them).
  const missions = useMissions(assigneeId, { enabled: isToday && !!assigneeId });
  // The ✨ effort-stars chip in the day-head needs the kid's active hero to skin the glyph (ADR-0103).
  const hasFeature = useFeature();
  const playEnabled = hasFeature(Feature.Play);
  const { hero } = useHero({ enabled: playEnabled && isToday && !!assigneeId });

  // Build the strip's per-day dots from the week agenda. The kid strip dots must stay in LOCKSTEP with
  // this page's own 3-item legend (done=green · wait=gold · planned=muted), so we normalize each status
  // to one of those three buckets BEFORE the shared WeekStrip maps it to a dot tone (the shared DOT_TONE
  // — guardian-shared — paints `rejected` gold like "awaiting" and `skipped` green like "done", which
  // would contradict the kid legend). Folding `rejected`/`missed` under `planned` keeps a rejected chore
  // off the "awaiting a grown-up" color; `skipped` stays in the done bucket (the kid's own done-set).
  const stripDays = useMemo(
    () =>
      (week.data?.days ?? []).map((d) => ({
        localDate: d.localDate,
        statuses: d.occurrences.map((o) => toLegendStatus(o.status)),
      })),
    [week.data],
  );

  const dayTitle = isToday
    ? t('today.kidTitle')
    : selectedDate > today
      ? t('today.upcoming')
      : t('today.earlier');

  return (
    <div className="kid-page kid-home flex flex-col gap-4">
      {/* Shared kid header (ADR-0113): the day title + formatted date, with the kid's all-currencies
          summary in the standard balance slot (the complete view also lives on the Wallet). */}
      <KidPageHeader
        title={dayTitle}
        // `parseLocalDate` already resolved `selectedDate` to a stable NOON-UTC instant representing
        // that exact household-local civil day (see its doc comment). Formatting it against the REAL
        // household `timeZone` here would re-apply a second timezone conversion on top of an already
        // timezone-resolved value — for any household in a UTC+13/+14 zone (a real, selectable IANA
        // zone e.g. Pacific/Kiritimati) that pushes noon UTC into the NEXT calendar day, rolling the
        // displayed date forward by one. Format as UTC instead: noon UTC read back in UTC is always
        // the same calendar day, by construction — no further zone conversion is needed or correct.
        lead={formatDay(locale, parseLocalDate(selectedDate), 'UTC')}
        balance={
          isToday && balance.data ? (
            <div className="flex max-w-[200px] flex-wrap items-center justify-end gap-1.5">
              <span
                className="coin-chip"
                role="img"
                aria-label={t('common.coinsLabel', { count: balance.data.coins })}
              >
                <span className="coin" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="9" fill="#FFEFB8" />
                    <path
                      d="M12 7.5v9M9.5 9.8h4a1.6 1.6 0 0 1 0 3.2h-4"
                      stroke="#A9760E"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                {balance.data.coins}
              </span>
              {playEnabled && (
                <HeroCurrencyProvider archetypeId={hero?.archetypeId ?? null}>
                  <ShopStarBalance stars={balance.data.stars} />
                </HeroCurrencyProvider>
              )}
              {balance.data.cash > 0 && (
                <span className="coin-chip" role="img" aria-label={t('wallet.cash')}>
                  <span className="coin cash" aria-hidden="true">
                    <CashIcon />
                  </span>
                  <Money amountMinor={balance.data.cash} />
                </span>
              )}
            </div>
          ) : undefined
        }
      />

      {/* ===== week navigator + 7-day strip (today highlighted). Home always shows a day, so re-tapping
          the selected day is a no-op (no "whole week" mode here, unlike the parent calendar). ===== */}
      <div className="card weeknav">
        <div className="weeknav-top">
          <button
            type="button"
            className="wk-arrow"
            aria-label={t('calendar.prevWeek')}
            onClick={() => setSelectedDate((d) => addDaysISO(d, -7))}
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M15 6l-6 6 6 6"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {isToday ? (
            <span className="wk-label">{t('calendar.thisWeek')}</span>
          ) : (
            <button type="button" className="wk-label" onClick={() => setSelectedDate(today)}>
              {t('today.jumpToday')}
            </button>
          )}
          <button
            type="button"
            className="wk-arrow"
            aria-label={t('calendar.nextWeek')}
            onClick={() => setSelectedDate((d) => addDaysISO(d, 7))}
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M9 6l6 6-6 6"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        {week.isPending ? (
          <LoadingState />
        ) : week.isError ? (
          <ErrorState onRetry={() => void week.refetch()} />
        ) : (
          <WeekStrip
            days={stripDays}
            today={today}
            selected={selectedDate}
            onSelect={(d) => d && setSelectedDate(d)}
          />
        )}
      </div>

      {/* ===== status legend (mockup "legend") ===== */}
      <div className="legend" aria-hidden="true">
        <span>
          <i className="dot-done" />
          {t('calendar.legendDone')}
        </span>
        <span>
          <i className="dot-wait" />
          {t('calendar.legendApprove')}
        </span>
        <span>
          <i className="dot-plan" />
          {t('calendar.legendPlanned')}
        </span>
      </div>

      {/* ===== TODAY only: the one-line HERO presence strip — the "now" surface of the approved mockup
          (02-home), not belonging to a past/future day. (The command-center-styled FamilyQuestCard +
          WeekRecap are NOT part of the kid Home mockup.) ===== */}
      {isToday && <HeroPresence />}

      {/* ===== TODAY only: the cozy kid view of the SHARED family quest (its own kid-skinned card, not
          the command-center FamilyQuestCard). Renders nothing unless a quest is active, so a quiet day
          stays uncluttered; the "now" pitch-in tap belongs to today like the hero strip. ===== */}
      {isToday && <KidQuestCard />}

      {/* ===== today's chores — occurrence cards grouped into Morning / Afternoon / Evening ===== */}
      {day.isPending ? (
        <LoadingState />
      ) : day.isError ? (
        <ErrorState onRetry={() => void day.refetch()} />
      ) : (
        <KidDayBoard
          day={day.data}
          date={selectedDate}
          assigneeId={assigneeId}
          isToday={isToday}
          canComplete={isToday && can(Perm.OccurrenceComplete)}
        />
      )}

      {/* ===== Missions DIGEST (ADR-0141) — a compact taste of the anytime-mission backlog ("read book
          X"), TODAY only. The full browsable shelf lives on /missions ("See all"), so the Home stays calm
          no matter how many missions pile up. Renders nothing until the child has an open mission. ===== */}
      {isToday &&
        hasFeature(Feature.Chores) &&
        missions.data &&
        missions.data.missions.length > 0 && (
          <MissionsDigest
            missions={missions.data.missions}
            assigneeId={assigneeId}
            canComplete={can(Perm.OccurrenceComplete)}
          />
        )}

      {/* A Habits section in the day flow — shown only on today, once the child actually has habits, so
          the day screen never carries an empty block (the dedicated Habits tab owns the empty state). */}
      {isToday && canCheckHabits && habits.data && habits.data.habits.length > 0 && (
        <section className="flex flex-col gap-2">
          <h2 className="bucket-h">{t('habits.sectionTitle')}</h2>
          <HabitBoard today={habits.data} canCheck />
        </section>
      )}
    </div>
  );
}

/**
 * Normalize an occurrence status to one of the kid strip's THREE legend buckets (done · wait · planned),
 * so the week-strip dots never paint a status the legend doesn't explain. The shared {@link WeekStrip}
 * maps a status through the guardian-shared `DOT_TONE`, where `rejected` is gold (= "awaiting") and
 * `skipped`/`approved` are green; on the kid surface a rejected chore is "still to do" (muted), not
 * awaiting a grown-up. We collapse the not-actioned/not-done statuses onto `planned` so the kid strip
 * and this page's legend stay in lockstep, without touching the shared widget.
 */
function toLegendStatus(status: OccurrenceResponseStatus): OccurrenceResponseStatus {
  switch (status) {
    case 'approved':
    case 'skipped':
      return 'approved'; // done bucket (green)
    case 'pending_approval':
      return 'pending_approval'; // wait bucket (gold)
    default:
      return 'planned'; // planned/missed/rejected → "not done" (muted)
  }
}

// The kid-facing "done" (incl. pending_approval) is one shared helper, so the board's check state and
// the kid's "Chores today" progress can never disagree.
const isDone = isChoreDoneByChild;

/** Status pill tone classes mirroring the real StatePill, ported to the mockup's `.st-*` design. */
const ST_TONE: Record<OccurrenceResponseStatus, string> = {
  approved: 'st-done',
  pending_approval: 'st-wait',
  planned: 'st-plan',
  skipped: 'st-skip',
  missed: 'st-miss',
  rejected: 'st-try',
};
const ST_GLYPH: Record<OccurrenceResponseStatus, string> = {
  approved: '✓',
  pending_approval: '◷',
  planned: '○',
  skipped: '–',
  missed: '·',
  rejected: '↺',
};

/**
 * The selected day's chores, rebuilt to the mockup's occurrence-card design (`.occ` rows under
 * `.bucket-h` time-of-day headers): chore glyph tile, title, a gold coin tag + a state pill, and the
 * right-hand control that depends on the chore's state — the big round complete control, a "Try again"
 * (redo) on a rejected chore, or an "Add photo" link leading to the chore detail for photo-proof
 * chores. Reuses the SAME optimistic complete/redo features the page already shipped, so completing a
 * chore bumps the list + coin balance together and reconciles on settle. A PAST/FUTURE day reads
 * calmly ("nothing scheduled"); on today, an all-done day shows the kind footer (never a dead end).
 */
function KidDayBoard({
  day,
  date,
  assigneeId,
  isToday,
  canComplete,
}: {
  day: DayTasksResponse;
  date: string;
  assigneeId: string;
  isToday: boolean;
  canComplete: boolean;
}): React.ReactElement {
  const { t } = useTranslation('family');
  const navigate = useNavigate();
  const { complete, isOccurrencePending, error } = useCompleteTaskAction({
    date,
    assigneeId,
    childId: assigneeId,
  });
  // A rejected chore can't be completed again (the domain guards `complete` to `planned`); it must be
  // re-submitted via redo (→ pending_approval). Same optimistic surface, keyed on the same day list.
  const {
    redo,
    isOccurrencePending: isRedoPending,
    error: redoError,
  } = useRedoTaskAction({
    date,
    assigneeId,
  });
  // Undo a just-completed chore (ADR-0141) — the kid can un-tap a mistake without a grown-up.
  const {
    uncomplete,
    isOccurrencePending: isUndoPending,
    error: undoError,
  } = useUncompleteTaskAction({ date, assigneeId, childId: assigneeId });
  // `error`/`redoError` are two independent mutations' state — neither hook resets the OTHER's error,
  // so a stale complete-task failure can otherwise permanently mask a fresh redo-task failure (or vice
  // versa). Track which action fired LAST and only surface that one's error in the banner.
  const [lastAction, setLastAction] = useState<'complete' | 'redo' | 'undo' | null>(null);

  const groups = [...day.groups].sort(
    (a, b) => BUCKET_ORDER.indexOf(a.timeOfDay) - BUCKET_ORDER.indexOf(b.timeOfDay),
  );
  const total = groups.reduce((n, g) => n + g.occurrences.length, 0);

  if (total === 0) {
    // A quiet past/future day reads calmly; today's empty isn't a dead end — point to rewards to spend.
    if (!isToday) {
      return <EmptyState art="empty.calendar" glyph="🌤️" message={t('calendar.noTasks')} />;
    }
    return (
      <EmptyState
        art="empty.calendar"
        glyph="🌤️"
        message={t('today.allDone')}
        action={
          <Link to="/rewards" className="btn btn-ghost">
            {t('today.allDoneCta')}
          </Link>
        }
      />
    );
  }

  // Surface a completion OR redo failure (after the optimistic rollback) instead of failing silently.
  // Surface ONLY whichever action the kid invoked MOST RECENTLY — never fall back to the OTHER
  // mutation's error, or a stale failure from one action would resurface and mask a fresh SUCCESS on
  // the other (e.g. complete fails, then a later unrelated redo succeeds: `redoError` is null, but
  // falling back to the old `error` would keep showing the completion failure forever).
  const errorMessage = (() => {
    const actionError =
      lastAction === 'redo'
        ? redoError
        : lastAction === 'undo'
          ? undoError
          : lastAction === 'complete'
            ? error
            : (error ?? redoError ?? undoError);
    if (actionError == null) return null;
    const m = resolveErrorMessage(actionError);
    return tDynamic(t, m.key, m.params);
  })();

  // The kind footer: only on today, only once every chore is done-by-the-child (never a guilt nudge
  // while work remains). Reuses the existing "all done" copy — no FOMO, no timer, no ranking.
  const allDone = isToday && groups.every((g) => g.occurrences.every((o) => isDone(o.status)));

  return (
    <div className="flex flex-col gap-1">
      {errorMessage && (
        <p role="alert" className="occ-error">
          {errorMessage}
        </p>
      )}

      {groups.map((group) =>
        group.occurrences.length === 0 ? null : (
          <section key={group.timeOfDay}>
            <h2 className="bucket-h">{t(`timeOfDay.${group.timeOfDay}`)}</h2>
            <div className="occ-list">
              {group.occurrences.map((occ) => (
                <OccCard
                  key={occ.id}
                  occ={occ}
                  date={date}
                  assigneeId={assigneeId}
                  canComplete={canComplete}
                  completing={isOccurrencePending(occ.id)}
                  redoing={isRedoPending(occ.id)}
                  undoing={isUndoPending(occ.id)}
                  onComplete={() => {
                    setLastAction('complete');
                    complete(occ);
                  }}
                  onRedo={() => {
                    setLastAction('redo');
                    redo(occ.id);
                  }}
                  onUndo={() => {
                    setLastAction('undo');
                    uncomplete(occ);
                  }}
                  // A PROJECTED occurrence (a computed day, not a persisted row) has no chore-detail
                  // record to open yet — the API rejects its synthetic `virtual:` id as a route param.
                  onOpen={occ.projected ? undefined : () => void navigate(`/chore/${occ.id}`)}
                />
              ))}
            </div>
          </section>
        ),
      )}

      {allDone && (
        <div className="all-good">
          <p>{t('today.allDone')}</p>
        </div>
      )}
    </div>
  );
}

/** Whether the kid may UNDO this occurrence (ADR-0141): it's awaiting a grown-up (`pending_approval`,
 * nothing credited) or it auto-credited (`approved` with no explicit approver). A grown-up's manual
 * approval (`approvedById` set) is never kid-undoable. Mirrors the server's `uncomplete` rule. */
function canUndoOcc(occ: OccurrenceResponse): boolean {
  return (
    occ.status === 'pending_approval' || (occ.status === 'approved' && occ.approvedById === null)
  );
}

/** A single chore occurrence card — the `.occ` row from the mockup, wired to the real controls. */
function OccCard({
  occ,
  date,
  assigneeId,
  canComplete,
  completing,
  redoing,
  undoing = false,
  onComplete,
  onRedo,
  onUndo,
  onOpen,
}: {
  occ: OccurrenceResponse;
  /** The board's local date + assignee — the context a virtual photo-proof capture completes against. */
  date: string;
  assigneeId: string;
  canComplete: boolean;
  completing: boolean;
  redoing: boolean;
  undoing?: boolean;
  onComplete: () => void;
  onRedo: () => void;
  /** Undo a just-completed chore back to "to do" (ADR-0141). Absent ⇒ no undo affordance. */
  onUndo?: () => void;
  /** Undefined for a PROJECTED occurrence — there is no chore-detail record to open yet. */
  onOpen: (() => void) | undefined;
}): React.ReactElement {
  const { t } = useTranslation('family');
  const done = isDone(occ.status);
  const showUndo = done && !!onUndo && canUndoOcc(occ);
  const coinLabel = t('common.coinsLabel', { count: occ.coinValue });

  // The right-hand control depends on the chore's state, mirroring the real TaskBoard FSM exactly.
  const control = (() => {
    if (!canComplete) return null;
    if (occ.status === 'missed') {
      // A forgotten day is never a dead end: route to the chore detail's gentle backfill (ADR-0040).
      return (
        <div className="occ-side">
          <button
            type="button"
            className="occ-act"
            onClick={onOpen}
            aria-label={t('today.logItAriaLabel', { chore: occ.title })}
          >
            {t('today.logIt')}
          </button>
        </div>
      );
    }
    if (occ.status === 'rejected' && occ.requiresPhotoProof) {
      // A rejected PHOTO chore needs a fresh photo (rejection purged the old one) → the detail page. The
      // control is the SAME round camera as a photo-proof mission tile, so the two surfaces read alike.
      return (
        <button
          type="button"
          className="check cam"
          onClick={onOpen}
          aria-label={t('choreDetail.tryAgainAriaLabel', { chore: occ.title })}
          title={t('choreDetail.tryAgain')}
        >
          <CameraGlyph />
        </button>
      );
    }
    if (occ.status === 'rejected') {
      // A rejected chore offers "Try again" (redo → pending_approval) — a round retry, matching the tile.
      return (
        <button
          type="button"
          className="check redo"
          disabled={redoing}
          onClick={onRedo}
          aria-label={t('choreDetail.tryAgainAriaLabel', { chore: occ.title })}
          title={t('choreDetail.tryAgain')}
        >
          <RedoGlyph />
        </button>
      );
    }
    if (occ.requiresPhotoProof && occ.status === 'planned') {
      // A planned photo-proof card is a COMPUTED virtual day (ADR-0142) — there is no occurrence to open
      // yet. The camera captures + completes in one step, addressed by `(templateId, localDate)` (F8).
      return (
        <PhotoCompleteButton
          card={{
            templateId: occ.templateId,
            scheduledLocalDate: occ.scheduledLocalDate,
            assigneeId: occ.assigneeId,
            title: occ.title,
          }}
          ctx={{ date, assigneeId, childId: assigneeId }}
        />
      );
    }
    // A DONE chore shows the green check. When the kid may still undo it, the check ITSELF is the
    // toggle — tapping the green check un-does the mis-tap (no separate link) — mirroring a checkbox
    // (ADR-0141). A grown-up-approved chore isn't kid-undoable, so its check is a static indicator.
    if (done) {
      const doneCheck = (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M5 13l4 4L19 7"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
      return showUndo ? (
        <button
          type="button"
          className={`check is-done${undoing ? ' is-pending' : ''}`}
          disabled={undoing}
          aria-pressed={true}
          aria-label={t('today.undoDoneAriaLabel', { chore: occ.title })}
          title={t('today.undo')}
          onClick={onUndo}
        >
          {doneCheck}
        </button>
      ) : (
        <span className="check is-done" role="img" aria-label={t('status.done')}>
          {doneCheck}
        </span>
      );
    }
    // The big round optimistic complete control (planned, no photo → done).
    return (
      <button
        type="button"
        className={`check${completing ? ' is-pending' : ''}`}
        disabled={completing}
        aria-pressed={false}
        aria-label={t('today.markDoneAriaLabel', { chore: occ.title })}
        title={t('today.markDone')}
        onClick={onComplete}
      >
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M5 13l4 4L19 7"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0.55}
          />
        </svg>
      </button>
    );
  })();

  const titleContent = (
    <>
      <span className="occ-title">{occ.title}</span>
      <span className="occ-meta">
        {occ.coinValue > 0 && (
          <span className="occ-coin" role="img" aria-label={coinLabel}>
            <CoinIcon />
            {occ.coinValue}
          </span>
        )}
        <span className={`st ${ST_TONE[occ.status]}`}>
          <span aria-hidden="true">{ST_GLYPH[occ.status]}</span>
          {t(`status.${occ.status}`)}
        </span>
      </span>
      {/* A photo note mirrors the mockup: "photo submitted" once it's awaiting a grown-up, or
          "photo needed" while a photo-proof chore is still planned. */}
      {occ.requiresPhotoProof && occ.status === 'pending_approval' && (
        <span className="photo-note">
          <span className="ico" aria-hidden="true">
            📸
          </span>
          {t('photoProof.submittedHint')}
        </span>
      )}
      {occ.requiresPhotoProof && occ.status === 'planned' && (
        <span className="photo-note">
          <span className="ico" aria-hidden="true">
            📸
          </span>
          {t('today.photoNeeded')}
        </span>
      )}
    </>
  );

  return (
    <div className="card occ">
      <span className="occ-ic" aria-hidden="true">
        <ConceptIcon art={choreArt(occ.icon)} glyph={choreGlyph(occ.icon)} size={28} />
      </span>
      {/* A PROJECTED occurrence (a future plan computed from the schedule, no persisted row) renders as
          an inert row — there is no chore-detail record to open yet, mirroring the guardian-side
          OccurrenceCard's `projected ? <div> : <Link>` split. */}
      {occ.projected || !onOpen ? (
        <div className="occ-link">{titleContent}</div>
      ) : (
        <button type="button" className="occ-link" onClick={onOpen}>
          {titleContent}
        </button>
      )}
      {control}
    </div>
  );
}
